import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect, CFormCheck, CCardFooter, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CFormFeedback
} from '@coreui/react';

import { apiClient } from 'services/Api';
import DateInput from 'components/forms/DateInput';
import { jwtDecode } from 'jwt-decode';
import TextEditor from "components/forms/TextEditor";
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from 'components/globals/AlertNotification';

const InnovationAction = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const location = useLocation();
    const [toast, setToast] = useState(null);
    const [isEditing, setIsEditing] = useState(true);
    const [fieldErrors, setFieldErrors] = useState({});

    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const is_director = decodedToken.is_director;
    const user_id = decodedToken.user_id;

    const fetchInnovationActionDetails = async () => {
        let data = null; // Declare a variable to store API response
        try {
            const response = await apiClient.get(`/innovation-actions/${id}/`);
            data = response.data; // Store response data
            setFormData(data); // Update state
        } catch (error) {
            triggerAlert('Ação de inovação não foi encontrada', 'error');
            navigate("/acoes-inovacao"); // Redirect to /ideas on error
        } finally {
            setIsEditing(is_director); // Use the local variable, NOT formData
        }
    };

    useEffect(() => {
        if (!id) {
            setIsEditing(true);
            setFormData({ date: new Date().toISOString().split("T")[0], evaluation_date: '' });
        } else {
            fetchInnovationActionDetails();
        }
    }, [id, location.pathname]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        let updatedValue;
        if (type === "checkbox") {
            updatedValue = checked;
        } else if (value === "true") {
            updatedValue = true;
        } else if (value === "false") {
            updatedValue = false;
        } else if (value === "") {
            updatedValue = null;
        } else {
            updatedValue = value;
        }

        setFormData((prevFormData) => {
            const updatedFormData = {
                ...prevFormData,
                [name]: updatedValue,
            };

            // If efficiency is just now being set (and not blank), set evaluation_date
            if (name === "efficiency" && value && !prevFormData.evaluation_date) {
                updatedFormData.evaluation_date = new Date().toISOString().split("T")[0];
            }

            return updatedFormData;
        });

        setFieldErrors((prevErrors) => {
            const updated = { ...prevErrors };
            delete updated[name];
            return updated;
        });

        setIsEditing(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const cleanFormData = {
                ...formData,
                evaluation_date: formData.evaluation_date || null,
            };

            if (id) {
                await apiClient.put(`/innovation-actions/${id}/`, cleanFormData);
                triggerAlert('Ação de inovação atualizada com sucesso!', 'success');
                navigate(`/acoes-inovacao`);
            } else {
                await apiClient.post('/innovation-actions/', cleanFormData);
                triggerAlert('Ação de inovação atualizada com sucesso!', 'success');
                navigate('/acoes-inovacao');
            }
        } catch (error) {
            const errors = error.response?.data;
            let errorMessage = 'Erro ao gravar';

            if (errors && typeof errors === 'object') {
                setFieldErrors(errors); // <-- Save the full error object
            } else {
                setFieldErrors({});
            }

            setTimeout(() => {
                const firstInvalidInput = document.querySelector('input.is-invalid, textarea.is-invalid, select.is-invalid');
                if (firstInvalidInput) {
                    firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    firstInvalidInput.focus();
                }
            }, 50);


            setToast({ title: 'Ação de inovação', message: errorMessage, type: 'error' });
            console.error('Error submitting form:', error);
        }
    };


    if (formData !== null) {
        if (!isEditing) {
            const renderText = (text) => {
                return <div dangerouslySetInnerHTML={{ __html: text || "-" }} />;
            };

            const getLabel = (value) => {
                const labels = {
                    1: "Muito Baixo",
                    2: "Baixo",
                    3: "Elevado",
                    4: "Muito Elevado"
                };
                return labels[value] || "";
            };

            return (
                <div className="container mt-4">
                    <h4 className="text-center mb-5">Detalhes da Ação de inovação</h4>
                    {[
                        { label: "Código", value: formData.code },
                        { label: "Designação da Ideia", value: formData.designation },
                        { label: "Descrição da Ideia", value: renderText(formData.description) },
                        { label: "Descrição do impacto", value: renderText(formData.impact) },
                        { label: "Origem", value: formData.origin },
                        { label: "Data", value: formData.date },
                        {
                            label: "Natureza",
                            value: (
                                <>
                                    {formData.organizational && <div>Organizacional</div>}
                                    {formData.service && <div>Serviço</div>}
                                    {formData.marketing && <div>Marketing</div>}
                                    {formData.process && <div>Processo</div>}
                                </>
                            )
                        },
                        { label: "Tipo de ideia", value: formData.idea_type },
                        {
                            label: "Avaliação",
                            value: (
                                <CTable striped bordered hover>
                                    <CTableHead>
                                        <CTableRow>
                                            <CTableHeaderCell className="text-center col-md-2">Viabilidade</CTableHeaderCell>
                                            <CTableHeaderCell className="text-center col-md-2">Recursos</CTableHeaderCell>
                                            <CTableHeaderCell className="text-center col-md-1">Retorno</CTableHeaderCell>
                                            <CTableHeaderCell className="text-center col-md-1">Risco</CTableHeaderCell>
                                            <CTableHeaderCell className="text-center col-md-1">Prazo</CTableHeaderCell>
                                            <CTableHeaderCell className="text-center col-md-1">Alinhamento</CTableHeaderCell>
                                            <CTableHeaderCell className="text-center col-md-2" colSpan={2}>Avaliação</CTableHeaderCell>
                                        </CTableRow>
                                    </CTableHead>
                                    <CTableBody>
                                        <CTableRow>
                                            <CTableDataCell className="text-center">{getLabel(formData.c1)}</CTableDataCell>
                                            <CTableDataCell className="text-center">{getLabel(formData.c2)}</CTableDataCell>
                                            <CTableDataCell className="text-center">{getLabel(formData.c3)}</CTableDataCell>
                                            <CTableDataCell className="text-center">{getLabel(formData.c4)}</CTableDataCell>
                                            <CTableDataCell className="text-center">{formData.deadline}</CTableDataCell>
                                            <CTableDataCell className="text-center">{formData.alignment === true ? "Sim" : formData.alignment === false ? "Não" : ""}</CTableDataCell>
                                            <CTableDataCell className="text-center">{formData.evaluation_score}</CTableDataCell>
                                            <CTableDataCell className="text-center">{formData.evaluation}</CTableDataCell>
                                        </CTableRow>
                                    </CTableBody>
                                </CTable>
                            )
                        },
                        { label: "Data da Avaliação", value: formData.evaluation_date },
                        { label: "Gestor", value: formData.responsible_name },
                        ...(formData.idea_type === "Projeto IDI" ? [
                            { label: "Riscos/Limitações", value: renderText(formData.risks) },
                            { label: "Ações para minimizar riscos", value: renderText(formData.actions) },
                            { label: "Estado de arte", value: renderText(formData.state_of_art) },
                            { label: "Orçamento", value: formData.budget },
                            { label: "Tipo de projeto", value: formData.project_type },
                            { label: "Justificação", value: renderText(formData.justification) },
                        ] : []),

                        { label: "Decisão", value: formData.state },
                        { label: "Observações", value: renderText(formData.obs) },
                    ].map((item, index) => (
                        <CCard key={index} className="mb-3">
                            <CCardHeader>
                                <h5>{item.label}</h5>
                            </CCardHeader>
                            <CCardBody>
                                <div>{item.value}</div>
                            </CCardBody>
                        </CCard>
                    ))}

                    {/* Back button */}
                    <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
                        <CButton color="danger" className="text-white" onClick={() => navigate('/ideias')}>
                            Voltar
                        </CButton>
                    </div>
                </div>
            );
        }

        return (
            <div className="container mt-4">
                {/* Error Toast - Appears on submit failure */}
                <ToastNotification toast={toast} setToast={setToast} />

                <CCard>
                    <CCardHeader>
                        <h4>{id ? 'Editar Ação de Inovação' : 'Nova Ação de Inovação'}</h4>
                    </CCardHeader>
                    <CCardBody>
                        <CForm onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <CFormInput
                                    type="text"
                                    label={<strong>Iniciativa</strong>}
                                    name="name"
                                    value={formData.name ?? ''}
                                    onChange={handleInputChange}
                                    invalid={!!fieldErrors.name}
                                />
                                {fieldErrors.name && (
                                    <CFormFeedback invalid>{fieldErrors.name[0]}</CFormFeedback>
                                )}
                            </div>

                            <DateInput
                                label={<strong>Data arranque</strong>}
                                name="start_date"
                                value={formData.start_date ?? ''}
                                onChange={handleInputChange}
                            />

                            <CFormSelect
                                label={<strong>Caracter Recorrente ou Pontual</strong>}
                                name="occurrence_type"
                                value={formData.occurrence_type ?? ''}
                                onChange={handleInputChange}
                                className="mb-3"
                            >
                                <option value="">Selecione</option>
                                <option value="Recorrente">Recorrente</option>
                                <option value="Pontual">Pontual</option>
                            </CFormSelect>

                            <CFormSelect
                                label={<strong>Periodicidade</strong>}
                                name="periodicity"
                                value={formData.periodicity ?? ''}
                                onChange={handleInputChange}
                                className="mb-3"
                            >
                                <option value="">Selecione</option>
                                <option value="Semanal">Semanal</option>
                                <option value="Mensal">Mensal</option>
                                <option value="Anual">Anual</option>
                            </CFormSelect>

                            <div className="mb-3">
                                <CFormSelect
                                    label={<strong>Ativa</strong>}
                                    name="active"
                                    value={formData.active ?? ''}
                                    onChange={handleInputChange}
                                    invalid={!!fieldErrors.active}
                                >
                                    <option value="">Selecione</option>
                                    <option value="true">Sim</option>
                                    <option value="false">Não</option>
                                </CFormSelect>
                                {fieldErrors.active && (
                                    <CFormFeedback invalid>{fieldErrors.active[0]}</CFormFeedback>
                                )}
                            </div>

                            <DateInput
                                label={<strong>Data fecho</strong>}
                                name="end_date"
                                value={formData.end_date ?? ''}
                                onChange={handleInputChange}
                            />

                            <CFormSelect
                                label={<strong>Objetivos alcançados</strong>}
                                name="objectives_met"
                                value={formData.objectives_met ?? ''}
                                onChange={handleInputChange}
                                className="mb-3"
                            >
                                <option value="">Selecione</option>
                                <option value="true">Sim</option>
                                <option value="false">Não</option>
                            </CFormSelect>


                            <CFormSelect
                                label={<strong>Eficaz</strong>}
                                name="efficiency"
                                value={formData.efficiency ?? ''}
                                onChange={handleInputChange}
                                className="mb-3"
                            >
                                <option value="">Selecione</option>
                                <option value="true">Sim</option>
                                <option value="false">Não</option>
                            </CFormSelect>

                            <DateInput
                                label={<strong>Data de avaliação</strong>}
                                name="evaluation_date"
                                value={formData.evaluation_date ?? ''}
                                onChange={handleInputChange}
                            />

                            <TextEditor
                                label="Observações"
                                value={formData.obs ?? ''}
                                onChange={(value) =>
                                    handleInputChange({ target: { name: "obs", value } })
                                }
                                className="mb-3"
                            />
                        </CForm>
                    </CCardBody>
                    <CCardFooter className="d-flex justify-content-end gap-2">
                        <CButton color="danger" className="text-white" onClick={() => navigate("/acoes-inovacao")}>
                            Cancelar
                        </CButton>
                        <CButton color="success" className="text-white" type="submit" onClick={handleSubmit}>
                            Confirmar
                        </CButton>
                    </CCardFooter>
                </CCard>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <h2>Loading...</h2>
        </div>
    );
};

export default InnovationAction;
